/**
 * Signing out, completely, on the browser's side. ADR-038.
 *
 * The server revokes the session and tells the browser to forget its cookies,
 * cache and storage. This file is everything the page does itself, because a
 * header is not the whole of it:
 *
 *  - **What is in memory.** The query cache holds every ticket and customer the
 *    console has shown. A header cannot reach it; only this page can.
 *  - **Other tabs.** Signing out in one tab must sign out all of them, at once,
 *    rather than leaving a second tab showing the board until somebody clicks.
 *  - **The Back button.** A browser may keep a whole page in memory (the
 *    back/forward cache) and show it again exactly as it was — data and all —
 *    without asking the server anything. When that happens this page is told
 *    (`pageshow` with `persisted`), and it checks before showing anything.
 *  - **Browsers that ignore the header**, and the one case it is deliberately
 *    narrowed: a technician's actions taken offline and not yet sent are kept,
 *    because signing out must never destroy work that exists nowhere else.
 *
 * Everything takes its browser pieces as arguments so it can be tested without
 * a browser; `browserEnvironment()` supplies the real ones.
 */

/** Device preferences that are not about the person and survive a sign-out. */
export const PRESERVED_LOCAL_KEYS: readonly string[] = [
  // Having read the cookie notice is a fact about this browser, not a person.
  'gsn.cookies',
  // The front page's colour theme: chosen on the public page, before anybody
  // signed in.
  'gsn-dispatch-theme',
];

type ReadableStorage = Pick<Storage, 'getItem' | 'setItem'>;

/**
 * The preserved entries as they are now, to put back afterwards.
 *
 * `Clear-Site-Data: "storage"` empties localStorage wholesale, preferences
 * included, before the sign-out request even resolves. So they are read
 * before it is sent and written back once it has been.
 */
export function keepPreferences(storage: ReadableStorage | null): Array<[string, string]> {
  if (!storage) return [];
  const kept: Array<[string, string]> = [];
  for (const key of PRESERVED_LOCAL_KEYS) {
    try {
      const value = storage.getItem(key);
      if (value !== null) kept.push([key, value]);
    } catch {
      /* Storage refused. */
    }
  }
  return kept;
}

export function restorePreferences(
  storage: ReadableStorage | null,
  kept: ReadonlyArray<[string, string]>,
): void {
  if (!storage) return;
  for (const [key, value] of kept) {
    try {
      storage.setItem(key, value);
    } catch {
      /* Storage refused. */
    }
  }
}

/** The line other tabs listen on. */
export const SESSION_CHANNEL = 'gsn-session';

/** Where the sign-in screen finds the sentence explaining why it is showing. */
export const SIGNED_OUT_REASON_KEY = 'gsn:signed-out-reason';

type KeyedStorage = Pick<Storage, 'key' | 'length' | 'removeItem' | 'clear' | 'setItem'>;
type CacheStore = { keys(): Promise<string[]>; delete(key: string): Promise<boolean> };

export type ClientEnvironment = {
  localStorage: KeyedStorage | null;
  sessionStorage: KeyedStorage | null;
  caches: CacheStore | null;
  queryClient: { clear(): void };
};

/**
 * Forget everything this browser holds about whoever was signed in.
 *
 * Never throws: a browser refusing storage is not a reason for a sign-out to
 * stop half way.
 */
export async function wipeClientState(
  environment: ClientEnvironment,
  options: { keepOffline: boolean },
): Promise<void> {
  try {
    environment.queryClient.clear();
  } catch {
    /* Nothing cached. */
  }

  const local = environment.localStorage;
  if (local) {
    try {
      const doomed: string[] = [];
      for (let index = 0; index < local.length; index += 1) {
        const key = local.key(index);
        if (key !== null && !PRESERVED_LOCAL_KEYS.includes(key)) doomed.push(key);
      }
      for (const key of doomed) local.removeItem(key);
    } catch {
      /* Storage refused. */
    }
  }

  try {
    environment.sessionStorage?.clear();
  } catch {
    /* Storage refused. */
  }

  /*
   * The service worker's caches hold the technician page and the jobs it has
   * shown. Kept only when offline work is waiting, because those are what let
   * the page open again without a signal to send it.
   */
  if (environment.caches && !options.keepOffline) {
    try {
      const keys = await environment.caches.keys();
      await Promise.all(keys.map((key) => environment.caches!.delete(key)));
    } catch {
      /* Nothing to clear. */
    }
  }
}

export type SessionMessage = { type: 'signed-out' } | { type: 'signed-in' };

type ChannelLike = {
  postMessage(message: unknown): void;
  addEventListener(type: 'message', listener: (event: { data: unknown }) => void): void;
  removeEventListener(type: 'message', listener: (event: { data: unknown }) => void): void;
  close(): void;
};

/**
 * Tell the other tabs, and hear from them.
 *
 * BroadcastChannel reaches every tab of this site in this browser profile and
 * nothing else. Where it does not exist the console still signs itself out the
 * first time the server refuses one of its requests — later, but certainly.
 */
export function sessionChannel(
  create: ((name: string) => ChannelLike) | null = typeof BroadcastChannel === 'undefined'
    ? null
    : (name) => new BroadcastChannel(name) as unknown as ChannelLike,
) {
  let channel: ChannelLike | null = null;
  try {
    channel = create ? create(SESSION_CHANNEL) : null;
  } catch {
    channel = null;
  }

  return {
    announce(message: SessionMessage): void {
      try {
        channel?.postMessage(message);
      } catch {
        /* A closed channel: nothing to tell. */
      }
    },
    listen(onMessage: (message: SessionMessage) => void): () => void {
      if (!channel) return () => undefined;
      const listener = (event: { data: unknown }) => {
        const data = event.data as Partial<SessionMessage> | null;
        if (data && (data.type === 'signed-out' || data.type === 'signed-in')) {
          onMessage(data as SessionMessage);
        }
      };
      channel.addEventListener('message', listener);
      return () => channel?.removeEventListener('message', listener);
    },
    close(): void {
      try {
        channel?.close();
      } catch {
        /* Already closed. */
      }
    },
  };
}

/**
 * Paths whose pages belong to nobody in particular — a job link, a tracking
 * page, a signing link — and are not the console. Returning to one of those
 * from the back/forward cache needs no check.
 */
const PUBLIC_PREFIXES = [
  '/t/',
  '/track/',
  '/ticket/',
  '/sign/',
  '/landlord/',
  '/activate/',
  '/set-password/',
];

export function isConsolePath(pathname: string): boolean {
  if (pathname === '/login' || pathname === '/forgot-password') return false;
  return !PUBLIC_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

export type PageShowActions = {
  pathname: string;
  /** Whether the readable CSRF cookie is still there — i.e. not signed out. */
  hasSessionCookie(): boolean;
  hide(): void;
  /** Undo `hide`: the server could not be asked, so the page stays as it was. */
  show(): void;
  /**
   * Still signed in: load the page afresh rather than show the kept copy. It
   * may be somebody else's session by now — another person signed in on this
   * browser since — and a fresh load is the only copy certain to be theirs.
   */
  refresh(): void;
  /**
   * Ask the server whether this browser is still signed in: `yes`, `no` (it
   * answered 401), or `unknown` (it could not be reached, or failed).
   */
  stillSignedIn(): Promise<'yes' | 'no' | 'unknown'>;
  /** Go to the sign-in screen, replacing this history entry. */
  leave(): void;
};

/**
 * A page brought back from the back/forward cache.
 *
 * Shown as it was left: if it was the console, it has somebody's data on it.
 * So it is hidden first, synchronously, before the browser paints it:
 *
 *  - signed out — the CSRF cookie is gone, known without the network — or the
 *    server answers that there is no session: the sign-in screen;
 *  - still signed in: loaded again from the server rather than shown as kept;
 *  - **the server cannot be reached**: shown as it was. A technician pressing
 *    Back with no signal is still signed in as far as anybody knows, and
 *    sending them to a sign-in page they cannot use would strand them. Only an
 *    answer from the server ends anything.
 */
export async function onPageShow(event: { persisted: boolean }, actions: PageShowActions): Promise<void> {
  if (!event.persisted || !isConsolePath(actions.pathname)) return;

  actions.hide();
  if (!actions.hasSessionCookie()) {
    actions.leave();
    return;
  }

  let answer: 'yes' | 'no' | 'unknown';
  try {
    answer = await actions.stillSignedIn();
  } catch {
    answer = 'unknown';
  }
  if (answer === 'yes') actions.refresh();
  else if (answer === 'no') actions.leave();
  else actions.show();
}

/** The readable cookie that exists exactly while a session does. */
export function hasCsrfCookie(cookieHeader: string): boolean {
  return /(?:^|;\s*)(?:__Host-)?gsn_csrf=[^;]+/.test(cookieHeader);
}

/** The real browser's pieces, for `wipeClientState`. */
export function browserEnvironment(queryClient: { clear(): void }): ClientEnvironment {
  const safe = <T>(get: () => T): T | null => {
    try {
      return get();
    } catch {
      return null;
    }
  };
  return {
    queryClient,
    localStorage: safe(() => window.localStorage),
    sessionStorage: safe(() => window.sessionStorage),
    caches: safe(() => ('caches' in window ? window.caches : null)),
  };
}
